import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiRequest } from '../api/client.js';

type EstadoCapacidad = {
  readonly capability: string;
  readonly available: boolean;
  readonly provider: string | null;
  readonly model: string | null;
  readonly isMock: boolean;
  readonly missingConfiguration: string[];
  readonly detail: string | null;
};

type Vista = {
  readonly runtime: EstadoCapacidad[];
  readonly configured: {
    readonly capability: string;
    readonly provider: string;
    readonly model: string;
    readonly enabled: boolean;
    readonly updatedAt: string;
  }[];
};

type Carga =
  | { readonly tipo: 'cargando' }
  | { readonly tipo: 'listo'; readonly vista: Vista }
  | { readonly tipo: 'error'; readonly mensaje: string };

const NOMBRE: Record<string, string> = {
  TEXT: 'Texto (estructura de escenas)',
  IMAGE: 'Imágenes',
  VOICE: 'Voz (narración)',
  MUSIC: 'Música',
};

export function Ajustes(): React.JSX.Element {
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
  const [comprobando, setComprobando] = useState<string | null>(null);
  const [resultados, setResultados] = useState<Record<string, EstadoCapacidad>>({});

  const recargar = useCallback(async () => {
    try {
      setCarga({ tipo: 'listo', vista: await apiRequest<Vista>('/capabilities') });
    } catch (causa) {
      setCarga({
        tipo: 'error',
        mensaje: causa instanceof ApiError ? causa.message : 'No se pudo cargar la configuración.',
      });
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  async function comprobar(capability: string): Promise<void> {
    setComprobando(capability);
    try {
      const estado = await apiRequest<EstadoCapacidad>(`/capabilities/${capability}/check`, {
        method: 'POST',
      });
      setResultados((previos) => ({ ...previos, [capability]: estado }));
    } catch (causa) {
      if (causa instanceof ApiError) {
        setCarga({ tipo: 'error', mensaje: causa.message });
      }
    } finally {
      setComprobando(null);
    }
  }

  return (
    <section>
      <h1>Ajustes de IA</h1>
      <p>
        Las credenciales viven en el servidor y no se muestran aquí. Esta pantalla indica si cada
        capacidad está <strong>configurada</strong> o <strong>falta configuración</strong>, nunca el
        valor de una clave.
      </p>

      {carga.tipo === 'cargando' && <p role="status">Cargando…</p>}
      {carga.tipo === 'error' && <p role="alert">{carga.mensaje}</p>}

      {carga.tipo === 'listo' && (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--borde)' }}>
              <th scope="col">Capacidad</th>
              <th scope="col">Estado</th>
              <th scope="col">Proveedor / modelo</th>
              <th scope="col">Comprobar</th>
            </tr>
          </thead>
          <tbody>
            {carga.vista.runtime.map((estado) => {
              const comprobado = resultados[estado.capability];
              const efectivo = comprobado ?? estado;
              return (
                <tr key={estado.capability} style={{ borderBottom: '1px solid var(--borde-suave)' }}>
                  <td>{NOMBRE[estado.capability] ?? estado.capability}</td>
                  <td>
                    {efectivo.available ? (
                      <span style={{ color: 'var(--exito)' }}>
                        Configurada{efectivo.isMock ? ' (MOCK)' : ''}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--error)' }}>Falta configuración</span>
                    )}
                    {efectivo.detail !== null && (
                      <>
                        <br />
                        <small>{efectivo.detail}</small>
                      </>
                    )}
                    {efectivo.missingConfiguration.length > 0 && (
                      <>
                        <br />
                        <small>Falta: {efectivo.missingConfiguration.join(', ')}</small>
                      </>
                    )}
                  </td>
                  <td>
                    {efectivo.provider ?? '—'}
                    {efectivo.model !== null && (
                      <>
                        <br />
                        <small>{efectivo.model}</small>
                      </>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => void comprobar(estado.capability)}
                      disabled={comprobando !== null}
                    >
                      {comprobando === estado.capability ? 'Comprobando…' : 'Comprobar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {carga.tipo === 'listo' && (
        <>
          <p>
            <small>
              Comprobar consulta el catálogo de modelos del proveedor. No genera contenido ni
              consume crédito.
            </small>
          </p>
          {carga.vista.runtime.some((estado) => estado.isMock) && (
            <p role="note" style={{ border: '1px solid var(--aviso)', padding: '0.5rem' }}>
              Hay capacidades atendidas por el proveedor <strong>MOCK</strong>. Produce fixtures
              deterministas para desarrollo y <strong>no acredita</strong> una integración real.
            </p>
          )}
        </>
      )}
    </section>
  );
}
